import React, { useEffect, useState } from "react";
import { Container, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ChevronDown, FilterLeft, Search } from "react-bootstrap-icons";
import Dropdown from "react-bootstrap/Dropdown";
import Rating from "./Rating";
import {
  useDeleteReviewMutation,
  useReviewsQuery,
} from "../../graphql/reviews.hooks";
import { EditReviewModal } from "../../components/modal/editReview";
import Pagination from "../../components/pagination";
import { DeleteReviewModal } from "../../components/modal/deleteReview";
import NoReviewImg from "./img/no-review.png";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";

enum ReviewSortField {
  CreatedAt = "CREATED_AT",
  Rating = "RATING",
  Relevance = "RELEVANCE",
  RestaurantName = "RESTAURANT_NAME",
}

enum Order {
  Asc = "ASC",
  Desc = "DESC",
}

const Reviews = () => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortField, setSortField] = useState<ReviewSortField>(
    ReviewSortField.CreatedAt
  );
  const [order, setOrder] = useState<Order>(Order.Desc);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [activeFilterLabel, setActiveFilterLabel] = useState(
    t("review.newest")
  );
  const {
    data: reviewData,
    loading: reviewLoading,
    refetch,
  } = useReviewsQuery({
    variables: {
      page: currentPage,
      perPage: limit,
      search: searchTerm || null,
      sortField,
      order,
    },
  });
  const reviews = reviewData?.user?.reviews;

  const pageCount = reviews?.pageCount || 0;
  const totalItems = reviews?.totalCount || 0;
  const firstItemIndex = reviews
    ? (reviews.currentPage - 1) * reviews.itemsPerPage + 1
    : 0;
  const lastItemIndex = reviews
    ? Math.min(reviews.currentPage * reviews.itemsPerPage, totalItems)
    : 0;

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetch({
      search: searchTerm,
      page: 1,
      perPage: newLimit,
      sortField,
      order,
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    refetch({
      search: searchTerm,
      page: newPage,
      perPage: limit,
      sortField,
      order,
    });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      refetch({
        search: searchTerm,
        page: 1,
        perPage: limit,
        sortField,
        order,
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm, limit, sortField, order, refetch]);

  const handleEditClick = (uuid: string) => {
    setSelectedUuid(uuid);
    setShowModal(true);
  };
  const handleDeleteClick = (uuid: string) => {
    setSelectedUuid(uuid);
    setShowDeleteModal(true);
  };
  const [deleteReview] = useDeleteReviewMutation();

  const handleDelete = async (uuid: string) => {
    try {
      await deleteReview({ variables: { uuid } });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container>
      <h1 className="mb-5 fs-2 mt-5 mt-lg-0">{t("menu.reviews")}</h1>
      {reviews?.totalCount === 0 && searchTerm.trim() === "" ? (
        <>
          <div className="bg-white rounded-4 py-5">
            <div className="col-lg-6 mx-auto py-5 text-center">
              <img src={NoReviewImg} alt="No Review Img" />
              <p className="fw-bold fs-4">{t("review.noReview")}</p>
              <p className="mb-4 text-gray col-lg-10 mx-auto">
                {t("review.noReviewDescription")}
              </p>
              <div className="col-lg-4 mx-auto">
                <a
                  href={`${
                    lng === "en"
                      ? process.env.REACT_APP_ME_BOOKING_URL
                      : `${process.env.REACT_APP_ME_BOOKING_URL}/${lng}`
                  }`}
                  className="btn btn-primary py-3 fw-bold text-white w-100"
                >
                  {t("review.noReviewBtn")}
                </a>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 d-lg-flex justify-content-between">
            <div className="col-lg-3 mb-2 mb-lg-0">
              <div className="col-xxl-9 col-12">
                <div className="bg-white rounded-3 position-relative">
                  <input
                    type="text"
                    className="form-control border-0 py-2"
                    id="search"
                    placeholder={t("review.restaurantSearch")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="position-absolute translate-middle-y top-50 end-0 me-3" />
                </div>
              </div>
            </div>
            <div>
              <Dropdown className="bg-white rounded-3">
                <Dropdown.Toggle
                  id="dropdown-basic"
                  variant="link"
                  className="no-toggle text-dark text-decoration-none py-2 d-flex justify-content-between align-items-center w-100"
                >
                  <div>
                    <FilterLeft className="me-3" />
                    {activeFilterLabel}
                  </div>
                  <ChevronDown className="ms-2" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={activeFilterLabel === t("review.newest")}
                    onClick={() => {
                      setSortField(ReviewSortField.CreatedAt);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("review.newest"));
                    }}
                  >
                    {t("review.newest")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("review.oldest")}
                    onClick={() => {
                      setSortField(ReviewSortField.CreatedAt);
                      setOrder(Order.Asc);
                      setActiveFilterLabel(t("review.oldest"));
                    }}
                  >
                    {t("review.oldest")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("review.relevant")}
                    onClick={() => {
                      setSortField(ReviewSortField.Relevance);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("review.relevant"));
                    }}
                  >
                    {t("review.relevant")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("review.best")}
                    onClick={() => {
                      setSortField(ReviewSortField.Rating);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("review.best"));
                    }}
                  >
                    {t("review.best")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("review.worst")}
                    onClick={() => {
                      setSortField(ReviewSortField.Rating);
                      setOrder(Order.Asc);
                      setActiveFilterLabel(t("review.worst"));
                    }}
                  >
                    {t("review.worst")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="overflow-auto rounded-bottom-3 scroll-bar">
            <table style={{ minWidth: "1000px" }} className="table mb-0 fs-6">
              <thead>
                <tr>
                  <th className="border-0 fs-5 col-4 ps-4 py-3 table-start bg-nav">
                    {t("review.venueName")}
                  </th>
                  <th className="border-0 fs-5 py-3 col-2 bg-nav">
                    {t("review.rating")}
                  </th>
                  <th className="border-0 fs-5 py-3 col-3 col-xxl-4 bg-nav">
                    {t("review.details")}
                  </th>
                  <th className="border-0 fs-5 py-3 col-3 col-xxl-2 bg-nav table-end"></th>
                </tr>
              </thead>
              <tbody>
                {reviewLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : reviews?.items?.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      <p className="fw-bold fs-4">
                        {t("booking.noSearchResults")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  <>
                    {reviews?.items.map((review) => (
                      <tr key={review.uuid}>
                        <td className="py-4 ps-4">
                          <div className="d-flex">
                            <div className="me-3 restaurant-img col-3">
                              <img
                                loading="lazy"
                                className="img-fluid"
                                src={review.restaurant.logo}
                                alt=""
                              />
                            </div>

                            <div className="">
                              <p className="fs-5 mb-0 fw-bold">
                                {review?.restaurant.name}
                              </p>
                              <p className="mb-0 text-gray">
                                <small>
                                  {review?.restaurant.cuisines.map(
                                    (cuisine) => (
                                      <span key={cuisine.id}>
                                        {cuisine.name}
                                      </span>
                                    )
                                  )}
                                  {review?.restaurant.cuisines.length > 1 &&
                                    " / "}
                                  {review?.restaurant.cuisines.length > 0 &&
                                  review?.restaurant.types.length > 0
                                    ? " • "
                                    : ""}
                                  {review?.restaurant.types.map((type) => (
                                    <span key={type.id}>{type.name}</span>
                                  ))}
                                  {review?.restaurant.types.length > 1 && " / "}
                                </small>
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4">
                          <div className="d-flex align-items-center">
                            <span className="me-2">
                              {review?.overallRating?.toFixed(1)}
                            </span>
                            <Rating ratingData={review.overallRating} />
                          </div>
                        </td>
                        <td className="py-4">
                          {review?.comment || t("review.noComment")}
                        </td>
                        <td className="py-4 pe-4 ps-0">
                          <div className="row gx-3">
                            <div className="col-6">
                              <button
                                className="btn-primary btn fw-bold text-white py-2 w-100 fs-6"
                                onClick={() => handleEditClick(review?.uuid)}
                              >
                                {t("review.edit")}
                              </button>
                            </div>
                            <div className="col-6">
                              <button
                                className="btn-outline-primary btn py-2 w-100 fs-6"
                                onClick={() => handleDeleteClick(review?.uuid)}
                              >
                                {t("review.delete")}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <EditReviewModal
            showModal={showModal}
            onCancel={() => setShowModal(false)}
            reviews={reviews?.items ?? []}
            uuid={selectedUuid}
          />
          <DeleteReviewModal
            showModal={showDeleteModal}
            onCancel={() => setShowDeleteModal(false)}
            deleteReview={handleDelete}
            uuid={selectedUuid}
          />

          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </>
      )}
    </Container>
  );
};

export default Reviews;
